/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2024-01-12 16:27:08
 */
import React, { useState } from 'react'
import { Modal, message, Upload, UploadFile } from 'antd'
import Excel from 'exceljs'
import { useAppSelector } from '../../../../../app/hooks'
import { InboxOutlined } from '@ant-design/icons'

const { Dragger } = Upload

// 多级选项数据
interface CascadeData {
  cascade1: string
  cascade2: string
  cascade3: string
}

// 富文本对象
interface RichTextValue {
  richText: Array<{ text: string }>
}

interface UploadComponentProps {
  getExcelData: (data: CascadeData[]) => void
  reportSaClickEvent: (clickItem: string) => void
  reportSaUploadEvent: (content: string) => void
  afterUpload: (filename: string) => void
}

const UploadComponent: React.FC<UploadComponentProps> = ({
  getExcelData,
  reportSaClickEvent,
  reportSaUploadEvent,
  afterUpload
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const itemCnt = useAppSelector((state) => state.group.itemCnt)
  const config = useAppSelector((state) => state.main.config)
  const itemLimit = parseInt(
    String(itemCnt || config?.normal_group_option_limit || 1000)
  )

  const clearPage = () => {
    setFileList([])
  }

  const getCellPlainValue = (value: unknown): string => {
    if (typeof value === 'string' || typeof value === 'number') {
      return value.toString()
    }
    if (typeof value === 'object' && value !== null && 'richText' in value) {
      const richTextValue = value as RichTextValue
      const texts = richTextValue.richText.map((richText) => richText.text)
      return texts.join('')
    }
    return ''
  }

  const parseExcel = async (file: File) => {
    const workbook = new Excel.Workbook()
    const reader = new FileReader()
    reader.onloadend = function () {
      const arrayBuffer = reader.result as ArrayBuffer
      workbook.xlsx.load(arrayBuffer).then(function (wb) {
        const data: CascadeData[] = []
        const sheet = wb.worksheets[0]
        if (!sheet) {
          reportSaUploadEvent('formatError')
          Modal.error({
            title: '解析不到文件内的数据，请下载模板，按照模板填写信息后提交',
            onOk: clearPage,
            zIndex: 9999
          })
        } else {
          if (sheet.actualRowCount <= 2) {
            reportSaUploadEvent('noData')
            Modal.error({
              title: '表格中无数据，请按照模板填写信息后提交。',
              onOk: clearPage,
              zIndex: 9999
            })
            return false
          }
          if (sheet.actualRowCount > Number(itemLimit) + 2) {
            reportSaUploadEvent('exceedLimit')
            Modal.error({
              title: `一次最多导入${itemLimit}个选项！`,
              onOk: clearPage,
              zIndex: 9999
            })
          } else {
            wb.worksheets[0].eachRow(function (row, rowNumber) {
              if (rowNumber > 2) {
                const cascade1 = row.values[1]
                  ? getCellPlainValue(row.values[1])
                  : ''
                const cascade2 = row.values[2]
                  ? getCellPlainValue(row.values[2])
                  : ''
                const cascade3 = row.values[3]
                  ? getCellPlainValue(row.values[3])
                  : ''

                data.push({
                  cascade1: cascade1.trim().replace(/\n/g, ''),
                  cascade2: cascade2.trim().replace(/\n/g, ''),
                  cascade3: cascade3.trim().replace(/\n/g, '')
                })
              }
            })

            //数据解析完成调用函数传递列表
            getExcelData(data)
          }
        }
      })
    }
    reader.readAsArrayBuffer(file)
  }

  const beforeUpload = (file: File) => {
    reportSaClickEvent('chooseFile')
    const isExcelFile =
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.split('.')[1].indexOf('xls') > -1

    if (!isExcelFile) {
      message.error('文件格式错误，仅支持xls、xlsx文件！')
    } else if (file.size / 1024 / 1024 > 100) {
      message.error('上传文件过大，文件最大支持100MB！')
    } else {
      setFileList([file as unknown as UploadFile])
      afterUpload(file.name)
      parseExcel(file)
    }
    return false
  }

  const uploadProps = {
    accept:
      'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    name: 'file',
    multiple: false,
    beforeUpload,
    onRemove: clearPage,
    fileList
  }

  return (
    <Dragger {...uploadProps}>
      <div className="upload-drag-wrapper">
        <InboxOutlined
          style={{
            fontSize: 48,
            color: '#1890FF'
          }}
        />
        <p className="upload-text">点击选择文件或将文件拖拽至此处上传</p>
        <p className="upload-hint">仅支持xls、xlsx格式文件</p>
      </div>
    </Dragger>
  )
}

export default UploadComponent
